import type { ComponentType } from "react";
import type { CardType, VisualPayload } from "../../types";
import PokemonCard from "./PokemonCard";
import IVCard from "./IVCard";
import BestIVsCard from "./BestIVsCard";
import RankingCard from "./RankingCard";
import TopNCard from "./TopNCard";
import MovesetCard from "./MovesetCard";
import EvolutionCard from "./EvolutionCard";
import TypeCard from "./TypeCard";
import MetaCard from "./MetaCard";
import CompoundCard from "./CompoundCard";
import LoreCard from "./LoreCard";
import ComparisonCard from "./ComparisonCard";
import CandyCostCard from "./CandyCostCard";
import EmptyCard from "./EmptyCard";
import MoveDetailCard from "./MoveDetailCard";
import MoveComparisonCard from "./MoveComparisonCard";
import XLCandyCard from "./XLCandyCard";
import EliteTMCard from "./EliteTMCard";
import MoveLearnersCard from "./MoveLearnersCard";
import DefinitionCard from "./DefinitionCard";
import RocketLineupCard from "./RocketLineupCard";
import PowerupCostCard from "./PowerupCostCard";
import WeatherCard from "./WeatherCard";
import PvpCountersCard from "./PvpCountersCard";
import TypeAttackersCard from "./TypeAttackersCard";
import SeasonSummaryCard from "./SeasonSummaryCard";
import CupScheduleCard from "./CupScheduleCard";
import CupRulesCard from "./CupRulesCard";
import MoveChangeCard from "./MoveChangeCard";
import NewMovesCard from "./NewMovesCard";
import CupsTodayCard from "./CupsTodayCard";
import RaidBossesCard from "./RaidBossesCard";
import CommunityDaysCard from "./CommunityDaysCard";
import EggsTodayCard from "./EggsTodayCard";
import FieldResearchCard from "./FieldResearchCard";
import EventsCard from "./EventsCard";
import KnowledgeCard from "./KnowledgeCard";

type CardComponent = ComponentType<{ payload: VisualPayload }>;

/**
 * The one place a card_type is mapped to its component.  `satisfies`
 * makes TypeScript fail the build if a CardType is added without an entry.
 */
export const CARD_COMPONENTS = {
  pokemon_card: PokemonCard,
  iv_card: IVCard,
  best_ivs_card: BestIVsCard,
  ranking_card: RankingCard,
  top_n_card: TopNCard,
  moveset_card: MovesetCard,
  evolution_card: EvolutionCard,
  type_card: TypeCard,
  meta_card: MetaCard,
  lore_card: LoreCard,
  compound_card: CompoundCard,
  empty_card: EmptyCard,
  comparison_card: ComparisonCard,
  candy_cost_card: CandyCostCard,
  move_detail_card: MoveDetailCard,
  move_comparison_card: MoveComparisonCard,
  xl_candy_card: XLCandyCard,
  elite_tm_card: EliteTMCard,
  move_learners_card: MoveLearnersCard,
  definition_card: DefinitionCard,
  rocket_lineup_card: RocketLineupCard,
  powerup_cost_card: PowerupCostCard,
  weather_card: WeatherCard,
  pvp_counters_card: PvpCountersCard,
  type_attackers_card: TypeAttackersCard,
  // Phase 2 — Temporal Awareness.  Each season-intent card has a dedicated
  // component optimized for its use case; all five reuse the
  // SeasonHeaderStrip + shared sub-components from _seasonShared.tsx.
  season_summary_card: SeasonSummaryCard,
  cup_schedule_card: CupScheduleCard,
  cup_rules_card: CupRulesCard,
  move_change_card: MoveChangeCard,
  new_moves_card: NewMovesCard,
  // Phase 3 — "state of today" cards
  cups_today_card: CupsTodayCard,
  raid_bosses_today_card: RaidBossesCard,
  community_days_card: CommunityDaysCard,
  eggs_today_card: EggsTodayCard,
  field_research_today_card: FieldResearchCard,
  events_today_card: EventsCard,
  knowledge_card: KnowledgeCard,
} satisfies Record<CardType, CardComponent>;

interface CardRendererProps {
  payload: VisualPayload;
}

/**
 * Routes a VisualPayload to its card component; unknown types fall back to EmptyCard.
 *
 * Any card may lead with an `unreleased` section (backend
 * rag/cards/common.py:unreleased_section): it is shown as a note above the
 * card, and the card gets its sections without it.
 */
export default function CardRenderer({ payload }: CardRendererProps) {
  // Own-property check so a stray value such as "constructor" can't
  // resolve to an Object.prototype member.
  const Card: CardComponent = Object.prototype.hasOwnProperty.call(CARD_COMPONENTS, payload.card_type)
    ? CARD_COMPONENTS[payload.card_type]
    : EmptyCard;
  const note = payload.sections.find((s) => s.section === "unreleased");
  if (!note) return <Card payload={payload} />;
  const rest = { ...payload, sections: payload.sections.filter((s) => s !== note) };
  return (
    <div className="space-y-2">
      <p role="note" className="rounded-xl border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
        {String(note.text ?? "")}
      </p>
      <Card payload={rest} />
    </div>
  );
}
